import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Navbar, Footer } from './components/Layout';
import { Toaster } from './components/ui/sonner';
import './index.css';

// Pages
import HomePage from './pages/HomePage';
import NewsListPage from './pages/NewsListPage';
import NewsDetailPage from './pages/NewsDetailPage';
import TeamsPage from './pages/TeamsPage';
import TeamDetailPage from './pages/TeamDetailPage';
import TerminePage from './pages/TerminePage';
import GalleryPage from './pages/GalleryPage';
import SponsorsPage from './pages/SponsorsPage';
import ContactPage from './pages/ContactPage';
import ImpressumPage from './pages/ImpressumPage';
import DatenschutzPage from './pages/DatenschutzPage';
import MembershipPage from './pages/MembershipPage';
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import ProfilePage from './pages/ProfilePage';
import SpielplanPage from './pages/SpielplanPage';

// Admin Pages
import AdminLayout from './pages/admin/AdminLayout';
import AdminMembers from './pages/admin/AdminMembers';
import AdminTeams from './pages/admin/AdminTeams';
import AdminTournaments from './pages/admin/AdminTournaments';
import AdminTraining from './pages/admin/AdminTraining';
import AdminNews from './pages/admin/AdminNews';
import AdminSponsors from './pages/admin/AdminSponsors';
import AdminGallery from './pages/admin/AdminGallery';
import AdminMessages from './pages/admin/AdminMessages';
import AdminAttendance from './pages/admin/AdminAttendance';

const ProtectedRoute = ({ children, allowInactive }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Admin always has access; allowInactive lets inactive users through (e.g. profile)
  if (user.role !== 'admin' && !user.is_active && !allowInactive) {
    return (
      <div className="min-h-screen pt-24 pb-16 flex items-center justify-center px-4" data-testid="inactive-block-page">
        <div className="card-elevated max-w-md p-8 text-center">
          <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          </div>
          <h2 className="text-xl font-heading font-bold uppercase mb-3">Mitgliedschaft nicht aktiv</h2>
          <p className="text-text-secondary text-sm mb-4">
            Deine Mitgliedschaft ist noch nicht freigeschaltet. Bitte überweise den Mitgliedsbeitrag und warte auf die Bestätigung durch den Admin.
          </p>
          <div className="flex gap-3 justify-center">
            <a href="/profil" className="btn-primary inline-block px-6 py-2" data-testid="inactive-profile-link">Mein Profil</a>
            <a href="/" className="btn-outline inline-block px-6 py-2" data-testid="inactive-home-link">Zur Startseite</a>
          </div>
        </div>
      </div>
    );
  }
  
  return children;
};

const AppRouter = () => {
  const location = useLocation();

  // Scroll to top on every route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Handle Google OAuth callback
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <Navbar />
      <main className="pt-0">
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/news" element={<NewsListPage />} />
          <Route path="/news/:newsId" element={<NewsDetailPage />} />
          <Route path="/mannschaften" element={<TeamsPage />} />
          <Route path="/mannschaften/:teamId" element={<TeamDetailPage />} />
          <Route path="/sponsoren" element={<SponsorsPage />} />
          <Route path="/kontakt" element={<ContactPage />} />
          <Route path="/impressum" element={<ImpressumPage />} />
          <Route path="/datenschutz" element={<DatenschutzPage />} />
          <Route path="/mitglied-werden" element={<MembershipPage />} />
          
          {/* Protected Routes - only for registered/logged-in users */}
          <Route path="/termine" element={<ProtectedRoute><TerminePage /></ProtectedRoute>} />
          <Route path="/galerie" element={<ProtectedRoute><GalleryPage /></ProtectedRoute>} />
          <Route path="/spielplan" element={<ProtectedRoute><SpielplanPage /></ProtectedRoute>} />
          
          {/* Legacy redirects */}
          <Route path="/teams" element={<Navigate to="/mannschaften" replace />} />
          <Route path="/teams/:teamId" element={<Navigate to="/mannschaften" replace />} />
          <Route path="/rangliste" element={<Navigate to="/termine" replace />} />
          <Route path="/anwesenheit" element={<Navigate to="/termine" replace />} />
          <Route path="/turniere" element={<Navigate to="/termine" replace />} />
          <Route path="/turniere/:tournamentId" element={<Navigate to="/termine" replace />} />
          <Route path="/training" element={<Navigate to="/termine" replace />} />
          <Route path="/ueber-uns" element={<Navigate to="/kontakt" replace />} />
          <Route path="/agb" element={<Navigate to="/impressum" replace />} />
          
          {/* Auth Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          
          {/* Protected Routes */}
          <Route path="/profil" element={
            <ProtectedRoute allowInactive><ProfilePage /></ProtectedRoute>
          } />

          {/* Admin Routes */}
          <Route path="/admin" element={
            <ProtectedRoute><AdminLayout /></ProtectedRoute>
          }>
            <Route path="members" element={<AdminMembers />} />
            <Route path="teams" element={<AdminTeams />} />
            <Route path="tournaments" element={<AdminTournaments />} />
            <Route path="training" element={<AdminTraining />} />
            <Route path="news" element={<AdminNews />} />
            <Route path="sponsors" element={<AdminSponsors />} />
            <Route path="gallery" element={<AdminGallery />} />
            <Route path="messages" element={<AdminMessages />} />
            <Route path="attendance" element={<AdminAttendance />} />
          </Route>

          {/* 404 */}
          <Route path="*" element={
            <div className="min-h-screen pt-24 flex items-center justify-center">
              <div className="text-center">
                <h1 className="text-6xl font-heading font-bold text-primary mb-4">404</h1>
                <p className="text-text-secondary mb-8">Seite nicht gefunden</p>
                <a href="/" className="btn-primary inline-block px-8 py-3">Zur Startseite</a>
              </div>
            </div>
          } />
        </Routes>
      </main>
      <Footer />
      <Toaster position="top-right" richColors theme="dark" />
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
